import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageCircle, X, Send } from 'lucide-react';
import { api } from '../../api';

export const ChatbotWidget = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'assistant', content: t('chatbot.welcome') }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = React.useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = { role: 'user', content: input.trim() };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const response = await api.request('/chat', {
        method: 'POST',
        body: JSON.stringify({
          message: userMessage.content,
          conversation_history: messages.slice(-10)
        })
      });
      setMessages(prev => [...prev, { role: 'assistant', content: response.response }]);
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I encountered an error. Please try again.' }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const suggestedQuestions = [
    t('chatbot.suggestions.downloadContent'),
    t('chatbot.suggestions.scheduleWorkshop'),
    t('chatbot.suggestions.submitAttendance'),
    t('chatbot.suggestions.addTrainers'),
    t('chatbot.suggestions.changeLanguage'),
    t('chatbot.suggestions.viewAnalytics'),
    t('chatbot.suggestions.getSupport'),
    t('chatbot.suggestions.whatCanIDo')
  ];

  const visibleSuggestions = messages.length <= 2
    ? suggestedQuestions.slice(0, 4)
    : suggestedQuestions.slice(4, 8);

  const askSuggested = (question) => {
    setInput(question);
  };

  return (
    <>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 right-6 z-50 bg-levelup-teal text-white p-4 rounded-full shadow-lg hover:bg-levelup-dark transition-colors"
        data-testid="chatbot-toggle"
      >
        {isOpen ? <X size={24} /> : <MessageCircle size={24} />}
      </button>

      {isOpen && (
        <div className="fixed bottom-24 right-6 z-50 w-96 bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col" style={{ height: '500px' }}>
          <div className="bg-levelup-dark text-white p-4 rounded-t-2xl flex items-center space-x-3">
            <div className="w-10 h-10 bg-levelup-teal rounded-full flex items-center justify-center">
              <MessageCircle size={20} />
            </div>
            <div>
              <h3 className="font-semibold">{t('chatbot.title')}</h3>
              <p className="text-xs text-gray-300">AI Assistant</p>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.map((msg, idx) => (
              <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] p-3 rounded-lg text-sm ${
                  msg.role === 'user'
                    ? 'bg-levelup-teal text-white rounded-br-none'
                    : 'bg-gray-100 text-gray-800 rounded-bl-none'
                }`}>
                  {msg.content}
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-gray-100 text-gray-800 p-3 rounded-lg rounded-bl-none text-sm">
                  <span className="flex items-center space-x-1">
                    <span className="animate-pulse">{t('chatbot.thinking')}</span>
                  </span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {messages.length <= 3 && (
            <div className="px-4 pb-2">
              <p className="text-xs text-gray-500 mb-2">{t('chatbot.suggestedQuestions')}</p>
              <div className="flex flex-wrap gap-1">
                {visibleSuggestions.slice(0, 4).map((q, idx) => (
                  <button
                    key={idx}
                    onClick={() => askSuggested(q)}
                    className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-2 py-1 rounded-full transition-colors"
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="p-4 border-t">
            <div className="flex space-x-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={t('chatbot.placeholder')}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-levelup-teal focus:border-transparent"
                data-testid="chatbot-input"
              />
              <button
                onClick={sendMessage}
                disabled={!input.trim() || isLoading}
                className="bg-levelup-teal text-white p-2 rounded-full hover:bg-levelup-dark transition-colors disabled:opacity-50"
                data-testid="chatbot-send"
              >
                <Send size={18} />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
